// apps/provider/src/services/realtimeService.js
// Xano Realtime WebSocket client for push-based updates
//
// Multiplexes any number of handlers over a single channel subscription,
// tracks connection status (the SDK auto-reconnects on most close codes and
// channels rejoin themselves), and exposes onReconnect/ensureConnected so
// consumers can revalidate after a gap — Xano replays no missed messages.

import { XanoClient } from '@xano/js-sdk';

const LIVENESS_INTERVAL_MS = 60000;

class RealtimeService {
  client = null;
  // channelName -> { channel, handlers: Set<fn>, persistent: boolean }
  channels = new Map();
  reconnectListeners = new Set();
  lastStatus = null; // 'connected' | 'disconnected' | null (never connected yet)
  livenessTimer = null;

  initialize() {
    if (this.client) return;

    const connectionHash = import.meta.env.VITE_REALTIME_CONNECTION_HASH;
    if (!connectionHash) {
      console.warn('[RealtimeService] No VITE_REALTIME_CONNECTION_HASH configured — realtime disabled');
      return;
    }

    this.client = new XanoClient({
      instanceBaseUrl: import.meta.env.VITE_API_BASE_URL,
      realtimeConnectionHash: connectionHash,
    });

    // Liveness: a dead socket can't be revived by the visibility backstop when
    // the tab stays visible (two-monitor use) — periodically self-heal instead
    if (!this.livenessTimer) {
      this.livenessTimer = setInterval(() => {
        if (this.channels.size > 0 && this.lastStatus === 'disconnected') {
          this.ensureConnected();
        }
      }, LIVENESS_INTERVAL_MS);
    }
  }

  setAuthToken(token) {
    this.initialize();
    if (!this.client) return;
    this.client.setRealtimeAuthToken(token);
  }

  _handleStatus(status) {
    const previous = this.lastStatus;
    this.lastStatus = status;
    if (status === 'connected' && previous === 'disconnected') {
      // Reconnected after a gap — no message replay, so listeners must revalidate
      for (const listener of this.reconnectListeners) {
        try {
          listener();
        } catch (e) {
          console.error('[RealtimeService] onReconnect listener error:', e);
        }
      }
    }
  }

  _createChannel(channelName, entry) {
    const channel = this.client.channel(channelName);
    channel.on((message) => {
      if (message.action === 'connection_status') {
        this._handleStatus(message.payload?.status);
        return;
      }
      if (message.action === 'event') {
        const data = message.payload?.data || message.payload;
        for (const handler of entry.handlers) {
          try {
            handler(data);
          } catch (e) {
            console.error('[RealtimeService] handler error:', e);
          }
        }
      }
    });
    entry.channel = channel;
  }

  // persistent: keep the channel subscribed even when its last handler
  // unsubscribes — avoids destroy/recreate churn (and its event-loss gap) for
  // long-lived channels like the org appointment feed. Sticky once set.
  subscribe(channelName, onEvent, { persistent = false } = {}) {
    this.initialize();
    if (!this.client) return;

    let entry = this.channels.get(channelName);
    if (entry) {
      // Channel already live — just add the handler (no duplicate subscription)
      entry.handlers.add(onEvent);
      entry.persistent = entry.persistent || persistent;
      return;
    }

    entry = { channel: null, handlers: new Set([onEvent]), persistent };
    try {
      this._createChannel(channelName, entry);
      this.channels.set(channelName, entry);
    } catch (e) {
      // WebSocket may still be in CONNECTING state — safe to ignore
    }
  }

  // With a handler: remove just that handler, destroying the channel when none
  // remain (unless persistent). Without: remove the whole channel (legacy behavior).
  unsubscribe(channelName, onEvent) {
    const entry = this.channels.get(channelName);
    if (!entry) return;

    if (onEvent) {
      entry.handlers.delete(onEvent);
      if (entry.handlers.size > 0 || entry.persistent) return;
    }

    try {
      entry.channel?.destroy();
    } catch (e) {
      // Channel may not have finished connecting yet — safe to ignore
    }
    this.channels.delete(channelName);
  }

  unsubscribeAll() {
    for (const [name] of this.channels) {
      this.unsubscribe(name);
    }
  }

  // Register a callback fired after a disconnected -> connected transition.
  // Returns a removal function.
  onReconnect(listener) {
    this.reconnectListeners.add(listener);
    return () => this.reconnectListeners.delete(listener);
  }

  // The SDK does not auto-reconnect on all close codes (e.g. 1000/1001/1005 from
  // proxies or laptop sleep). Recreating the channels drives its observer count
  // 0 -> 1, which forces a fresh connect. Call on visibilitychange -> visible.
  ensureConnected() {
    if (!this.client || this.lastStatus !== 'disconnected') return;
    for (const [channelName, entry] of this.channels) {
      try {
        entry.channel?.destroy();
      } catch (e) {
        // ignore
      }
      try {
        this._createChannel(channelName, entry);
      } catch (e) {
        // WebSocket may still be in CONNECTING state — safe to ignore
      }
    }
  }
}

export const realtimeService = new RealtimeService();
